import { Transaction } from '../types';

export interface LoanCapacityResult {
  maxLoanAmount: number;
  maxMonthlyPayment: number;
  debtToIncomeRatio: number;
  recommendations: string[];
  loanSimulations: LoanSimulation[];
  riskLevel: 'low' | 'medium' | 'high';
  creditScore: number;
}

export interface LoanSimulation {
  loanAmount: number;
  duration: number; // en années
  interestRate: number;
  monthlyPayment: number;
  totalInterest: number;
  totalAmount: number;
}

export interface FinancialProfile {
  monthlyIncome: number;
  monthlyExpenses: number;
  existingDebts: number;
  savings: number;
  age: number;
  employmentType: 'cdi' | 'cdd' | 'freelance' | 'unemployed';
  hasGuarantor: boolean;
  downPayment: number;
}

export class LoanCalculatorService {
  private readonly MAX_DEBT_RATIO = 0.35; // 35% max recommandé
  private readonly MIN_REMAINING_INCOME = 800; // Reste à vivre minimum
  
  calculateLoanCapacity(
    transactions: Transaction[],
    profile: FinancialProfile
  ): LoanCapacityResult {
    // Analyser les revenus et dépenses automatiquement depuis les transactions
    const financialAnalysis = this.analyzeTransactions(transactions);
    
    // Utiliser les données du profil ou celles calculées
    const monthlyIncome = profile.monthlyIncome || financialAnalysis.averageMonthlyIncome;
    const monthlyExpenses = profile.monthlyExpenses || financialAnalysis.averageMonthlyExpenses;
    
    // Calculer les ratios financiers
    const netIncome = monthlyIncome - monthlyExpenses - profile.existingDebts;
    const debtToIncomeRatio = (monthlyExpenses + profile.existingDebts) / monthlyIncome;
    
    // Calculer la capacité d'endettement maximale
    const maxDebtPayment = Math.min(
      monthlyIncome * this.MAX_DEBT_RATIO,
      netIncome - this.MIN_REMAINING_INCOME
    );
    
    const availableForLoan = Math.max(0, maxDebtPayment - profile.existingDebts);
    
    // Calculer le montant d'emprunt maximal (sur 25 ans à 4% par défaut)
    const maxLoanAmount = this.calculateMaxLoanAmount(availableForLoan, 25, 0.04);
    
    // Générer des simulations
    const loanSimulations = this.generateLoanSimulations(availableForLoan);
    
    // Évaluer le niveau de risque
    const riskLevel = this.assessRiskLevel(profile, debtToIncomeRatio, financialAnalysis);
    
    // Calculer un score de crédit estimé
    const creditScore = this.estimateCreditScore(profile, financialAnalysis, debtToIncomeRatio);
    
    // Générer des recommandations
    const recommendations = this.generateRecommendations(
      profile, 
      debtToIncomeRatio, 
      financialAnalysis,
      riskLevel
    );

    return {
      maxLoanAmount: Math.round(maxLoanAmount),
      maxMonthlyPayment: Math.round(availableForLoan),
      debtToIncomeRatio: Math.round(debtToIncomeRatio * 100) / 100,
      recommendations,
      loanSimulations,
      riskLevel,
      creditScore
    };
  }

  private analyzeTransactions(transactions: Transaction[]) {
    const now = new Date();
    const sixMonthsAgo = new Date(now.getFullYear(), now.getMonth() - 6, 1);
    
    const recentTransactions = transactions.filter(t => new Date(t.date) >= sixMonthsAgo);
    
    const monthlyData: { [key: string]: { income: number; expenses: number } } = {};
    
    recentTransactions.forEach(transaction => {
      const monthKey = `${new Date(transaction.date).getFullYear()}-${new Date(transaction.date).getMonth()}`;
      
      if (!monthlyData[monthKey]) {
        monthlyData[monthKey] = { income: 0, expenses: 0 };
      }
      
      if (transaction.type === 'income') {
        monthlyData[monthKey].income += transaction.amount;
      } else {
        monthlyData[monthKey].expenses += transaction.amount;
      }
    });

    const months = Object.values(monthlyData);
    const averageMonthlyIncome = months.length > 0 
      ? months.reduce((sum, month) => sum + month.income, 0) / months.length 
      : 0;
    const averageMonthlyExpenses = months.length > 0 
      ? months.reduce((sum, month) => sum + month.expenses, 0) / months.length 
      : 0;

    // Analyser la stabilité des revenus
    const incomeStability = this.calculateIncomeStability(months.map(m => m.income));
    
    // Analyser les catégories de dépenses principales
    const expenseCategories = this.analyzeExpenseCategories(recentTransactions);

    return {
      averageMonthlyIncome: Math.round(averageMonthlyIncome),
      averageMonthlyExpenses: Math.round(averageMonthlyExpenses),
      incomeStability,
      expenseCategories,
      monthsAnalyzed: months.length
    };
  }

  private calculateIncomeStability(incomes: number[]): number {
    if (incomes.length < 2) return 0;
    
    const average = incomes.reduce((sum, income) => sum + income, 0) / incomes.length;
    const variance = incomes.reduce((sum, income) => sum + Math.pow(income - average, 2), 0) / incomes.length;
    const standardDeviation = Math.sqrt(variance);
    
    // Coefficient de variation (plus faible = plus stable)
    const coefficientOfVariation = standardDeviation / average;
    
    // Convertir en score de stabilité (0-100, 100 = très stable)
    return Math.max(0, Math.min(100, 100 - (coefficientOfVariation * 100)));
  }

  private analyzeExpenseCategories(transactions: Transaction[]) {
    const categories: { [key: string]: number } = {};
    
    transactions
      .filter(t => t.type === 'expense')
      .forEach(transaction => {
        categories[transaction.category] = (categories[transaction.category] || 0) + transaction.amount;
      });

    return Object.entries(categories)
      .map(([category, amount]) => ({ category, amount }))
      .sort((a, b) => b.amount - a.amount)
      .slice(0, 5);
  }

  private calculateMaxLoanAmount(monthlyPayment: number, durationYears: number, annualRate: number): number {
    const monthlyRate = annualRate / 12;
    const numberOfPayments = durationYears * 12;
    
    if (monthlyRate === 0) {
      return monthlyPayment * numberOfPayments;
    }
    
    return monthlyPayment * ((1 - Math.pow(1 + monthlyRate, -numberOfPayments)) / monthlyRate);
  }

  private generateLoanSimulations(maxMonthlyPayment: number): LoanSimulation[] {
    const simulations: LoanSimulation[] = [];
    const interestRates = [0.035, 0.04, 0.045]; // 3.5%, 4%, 4.5%
    const durations = [15, 20, 25]; // années
    
    durations.forEach(duration => {
      interestRates.forEach(rate => {
        const loanAmount = this.calculateMaxLoanAmount(maxMonthlyPayment, duration, rate);
        const totalAmount = maxMonthlyPayment * duration * 12;
        const totalInterest = totalAmount - loanAmount;
        
        simulations.push({
          loanAmount: Math.round(loanAmount),
          duration,
          interestRate: rate,
          monthlyPayment: Math.round(maxMonthlyPayment),
          totalInterest: Math.round(totalInterest),
          totalAmount: Math.round(totalAmount)
        });
      });
    });
    
    return simulations.sort((a, b) => b.loanAmount - a.loanAmount);
  }

  private assessRiskLevel(
    profile: FinancialProfile, 
    debtRatio: number, 
    analysis: any
  ): 'low' | 'medium' | 'high' {
    let riskScore = 0;
    
    // Ratio d'endettement
    if (debtRatio > 0.33) riskScore += 3;
    else if (debtRatio > 0.25) riskScore += 2;
    else riskScore += 1;
    
    // Type d'emploi
    switch (profile.employmentType) {
      case 'cdi': riskScore += 1; break;
      case 'cdd': riskScore += 2; break;
      case 'freelance': riskScore += 3; break;
      case 'unemployed': riskScore += 4; break;
    }
    
    // Stabilité des revenus
    if (analysis.incomeStability < 50) riskScore += 2;
    else if (analysis.incomeStability < 75) riskScore += 1;
    
    // Épargne disponible
    if (profile.savings < profile.monthlyIncome * 3) riskScore += 2;
    else if (profile.savings < profile.monthlyIncome * 6) riskScore += 1;
    
    // Âge
    if (profile.age > 55) riskScore += 2;
    else if (profile.age < 25) riskScore += 1;
    
    if (riskScore <= 4) return 'low';
    if (riskScore <= 7) return 'medium';
    return 'high';
  }

  private estimateCreditScore(profile: FinancialProfile, analysis: any, debtRatio: number): number {
    let score = 500; // Base score
    
    // Facteurs positifs
    if (profile.employmentType === 'cdi') score += 100;
    else if (profile.employmentType === 'cdd') score += 50;
    
    if (debtRatio < 0.2) score += 100;
    else if (debtRatio < 0.3) score += 50;
    
    if (analysis.incomeStability > 80) score += 80;
    else if (analysis.incomeStability > 60) score += 40;
    
    if (profile.savings > profile.monthlyIncome * 6) score += 100;
    else if (profile.savings > profile.monthlyIncome * 3) score += 50;
    
    if (profile.downPayment > 0) score += 50;
    if (profile.hasGuarantor) score += 50;
    
    // Facteurs négatifs
    if (profile.existingDebts > profile.monthlyIncome * 0.2) score -= 100;
    if (profile.age > 60) score -= 50;
    
    return Math.max(300, Math.min(850, score));
  }

  private generateRecommendations(
    profile: FinancialProfile, 
    debtRatio: number, 
    analysis: any,
    riskLevel: 'low' | 'medium' | 'high'
  ): string[] {
    const recommendations: string[] = [];
    
    if (debtRatio > 0.33) {
      recommendations.push("🔴 Votre taux d'endettement dépasse 33%. Réduisez vos dépenses avant d'emprunter.");
    } else if (debtRatio > 0.25) {
      recommendations.push("🟡 Votre taux d'endettement est élevé. Soyez prudent avec de nouveaux crédits.");
    } else {
      recommendations.push("🟢 Votre taux d'endettement est sain pour un emprunt.");
    }
    
    if (profile.savings < profile.monthlyIncome * 3) {
      recommendations.push("💰 Constituez une épargne de précaution (3-6 mois de charges) avant d'emprunter.");
    }
    
    if (analysis.incomeStability < 60) {
      recommendations.push("📈 Vos revenus sont instables. Les banques préfèrent des revenus réguliers.");
    }
    
    if (profile.employmentType !== 'cdi') {
      recommendations.push("📋 Un CDI améliorerait vos conditions d'emprunt.");
    }
    
    if (profile.downPayment === 0) {
      recommendations.push("🏠 Un apport personnel (10-20%) améliore les conditions d'emprunt.");
    }
    
    if (riskLevel === 'high') {
      recommendations.push("⚠️ Profil à risque élevé. Consultez un conseiller financier avant d'emprunter.");
    } else if (riskLevel === 'medium') {
      recommendations.push("⚡ Profil modéré. Étudiez bien les conditions avant de vous engager.");
    } else {
      recommendations.push("✅ Profil favorable aux emprunts. Négociez les meilleures conditions.");
    }
    
    // Recommandations d'optimisation
    if (analysis.monthsAnalyzed < 6) {
      recommendations.push("📊 Utilisez l'app plus longtemps pour des analyses plus précises.");
    }
    
    return recommendations;
  }

  // Calculer les mensualités pour un montant donné
  calculateMonthlyPayment(loanAmount: number, durationYears: number, annualRate: number): number {
    const monthlyRate = annualRate / 12;
    const numberOfPayments = durationYears * 12;
    
    if (monthlyRate === 0) {
      return loanAmount / numberOfPayments;
    }
    
    return loanAmount * (monthlyRate * Math.pow(1 + monthlyRate, numberOfPayments)) / 
           (Math.pow(1 + monthlyRate, numberOfPayments) - 1);
  }
}

export const loanCalculator = new LoanCalculatorService();
